import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogActions,
  Divider,
} from '@mui/material';

import {
  getEventRuntime,
  startEvent,
  endEvent,
  adjustScore,
  undoScore,
  resetGame,
} from '../../services/event-runtime.service';

type Runtime = {
  eventId: string;
  name: string;
  title?: string;
  gameType: string;
  phase?: 'pre' | 'during' | 'post';
  gamePeriod: number;
  maxGamePeriods: number;
  inPlay: boolean;
  periodStatus?: 'ready' | 'running' | 'ended';
  labels?: {
    currentPeriodName: string;
    nextPeriodName: string;
    startActionLabel: string;
    endActionLabel: string;
    gameOverLabel: string;
    continuous?: boolean;
  };
  score?: {
    left: number;
    right: number;
  };
  timer: {
    startedAt: number | null;
    accumulatedMs: number;
    running: boolean;
  };
  permissions: {
    canControlTimer: boolean;
    canViewTimer: boolean;
  };
  log: { ts: number; action: string; note?: string }[];
};

function toTitleCase(value: string) {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function splitTeamsFromTitle(title: string): { leftTeam: string; rightTeam: string } | null {
  const parts = title.split(/\s+vs\s+/i);

  if (parts.length !== 2) {
    return null;
  }

  return {
    leftTeam: parts[0].trim(),
    rightTeam: parts[1].trim(),
  };
}

function isRuntimeShape(value: any): value is Runtime {
  return !!value &&
    typeof value === 'object' &&
    typeof value.eventId === 'string' &&
    typeof value.name === 'string' &&
    typeof value.gameType === 'string' &&
    typeof value.gamePeriod === 'number' &&
    typeof value.maxGamePeriods === 'number' &&
    typeof value.inPlay === 'boolean' &&
    !!value.timer &&
    typeof value.timer === 'object' &&
    typeof value.timer.accumulatedMs === 'number' &&
    typeof value.timer.running === 'boolean' &&
    !!value.permissions &&
    typeof value.permissions === 'object' &&
    typeof value.permissions.canControlTimer === 'boolean' &&
    typeof value.permissions.canViewTimer === 'boolean' &&
    Array.isArray(value.log);
}

export default function EventServicePage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [runtime, setRuntime] = useState<Runtime | null>(null);
  const [displaySeconds, setDisplaySeconds] = useState(0);
  const [confirm, setConfirm] = useState<'start' | 'end' | 'reset' | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorText, setErrorText] = useState('');
  const [scoreBusy, setScoreBusy] = useState<'left-add' | 'left-sub' | 'right-add' | 'right-sub' | 'undo' | null>(null);

  const logPrefix = useMemo(() => `[event-service id=${String(id || '')}]`, [id]);

  const loadRuntime = useCallback(async (silent = false) => {
    if (!id) {
      console.warn(`${logPrefix} loadRuntime skipped because route param id is missing`);
      setErrorText('Missing event id in route');
      setLoading(false);
      return;
    }

    try {
      if (!silent) {
        setLoading(true);
      }

      const data = await getEventRuntime(id);

      if (!isRuntimeShape(data)) {
        setErrorText('Runtime response shape invalid');
        return;
      }

      setRuntime(data);
      setErrorText('');
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Unable to load event runtime';

      setErrorText(String(message));
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [id, logPrefix]);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setErrorText('Missing event id in route');
      return;
    }

    loadRuntime();

    const interval = setInterval(() => {
      loadRuntime(true);
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [id, loadRuntime]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!runtime) return;

      const { startedAt, accumulatedMs, running } = runtime.timer;

      let total = accumulatedMs;

      if (running && startedAt) {
        total += Date.now() - startedAt;
      }

      setDisplaySeconds(Math.floor(total / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [runtime]);

  useEffect(() => {
    if (!runtime) return;

    const { startedAt, accumulatedMs, running } = runtime.timer;

    let total = accumulatedMs;

    if (running && startedAt) {
      total += Date.now() - startedAt;
    }

    setDisplaySeconds(Math.floor(total / 1000));
  }, [runtime]);

  if (loading && !runtime) {
    return (
      <Box p={2}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!runtime) {
    return (
      <Box p={2}>
        <Typography color="error" sx={{ mb: 2 }}>
          {errorText || 'Runtime is empty on the client'}
        </Typography>

        <Button variant="outlined" onClick={() => navigate(-1)}>
          Back
        </Button>
      </Box>
    );
  }

  const minutes = Math.floor(displaySeconds / 60);
  const seconds = displaySeconds % 60;
  const isAdmin = runtime.permissions.canControlTimer;

  const phase =
    runtime.phase ||
    (runtime.gamePeriod === 0 && !runtime.inPlay
      ? 'pre'
      : runtime.gamePeriod >= runtime.maxGamePeriods && !runtime.inPlay
      ? 'post'
      : 'during');

  const phaseLabel = phase === 'pre' ? 'Pre-game' : phase === 'post' ? 'Post-game' : 'During';

  const currentPeriodName = runtime.labels?.currentPeriodName || 'Period';

  const nextPeriodNumber =
    runtime.gamePeriod === 0
      ? 1
      : Math.min(runtime.gamePeriod + 1, runtime.maxGamePeriods);

  const nextPeriodName =
    runtime.labels?.nextPeriodName || `Period ${nextPeriodNumber}`;

  const scoreLeft = runtime.score?.left ?? 0;
  const scoreRight = runtime.score?.right ?? 0;

  const teams = splitTeamsFromTitle(runtime.name || '');
  const leftTeamName = teams?.leftTeam || 'Team A';
  const rightTeamName = teams?.rightTeam || 'Team B';

  const isHalfTime =
    phase === 'during' &&
    !runtime.inPlay &&
    runtime.gamePeriod > 0 &&
    runtime.gamePeriod < runtime.maxGamePeriods;

  const isFullTime = phase === 'post';
  const showScorePanel = phase !== 'pre' && phase !== 'post';

  const scoreTitle = teams
    ? `${leftTeamName} ${scoreLeft} - ${scoreRight} ${rightTeamName}`
    : `${runtime.name} ${scoreLeft} - ${scoreRight}`;

  const titleText = phase === 'pre'
    ? runtime.name
    : isHalfTime
    ? `HT: ${scoreTitle}`
    : isFullTime
    ? `FT: ${scoreTitle}`
    : scoreTitle;

  const periodSummary = runtime.labels?.continuous
    ? currentPeriodName
    : `${currentPeriodName}`;

  const primaryAction: 'start' | 'end' | null =
    phase === 'post'
      ? null
      : runtime.inPlay
      ? 'end'
      : 'start';

  const primaryActionLabel =
    primaryAction === 'start'
      ? runtime.labels?.startActionLabel || `Start ${runtime.gamePeriod === 0 ? currentPeriodName : nextPeriodName}`
      : primaryAction === 'end'
      ? runtime.labels?.endActionLabel || 'End Period'
      : runtime.labels?.gameOverLabel || 'Game Over';

  const handleScore = async (
    side: 'left' | 'right',
    delta: number,
    busyKey: 'left-add' | 'left-sub' | 'right-add' | 'right-sub',
  ) => {
    if (!id) return;

    try {
      setScoreBusy(busyKey);
      const data = await adjustScore(id, side, delta);

      if (!isRuntimeShape(data)) {
        setErrorText('Score action returned invalid runtime shape');
        return;
      }

      setRuntime(data);
      setErrorText('');
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Score action failed';
      setErrorText(String(message));
    } finally {
      setScoreBusy(null);
    }
  };

  const handleConfirmedAction = async () => {
    if (!id || !confirm) {
      return;
    }

    try {
      const data =
        confirm === 'start'
          ? await startEvent(id)
          : confirm === 'end'
          ? await endEvent(id)
          : await resetGame(id);

      if (!isRuntimeShape(data)) {
        setErrorText(`${confirm} action returned invalid runtime shape`);
        return;
      }

      setRuntime(data);
      setErrorText('');
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Action failed';

      setErrorText(String(message));
    } finally {
      setConfirm(null);
    }
  };

  const handleUndo = async () => {
    if (!id) return;

    try {
      setScoreBusy('undo');
      const data = await undoScore(id);

      if (!isRuntimeShape(data)) {
        setErrorText('Undo action returned invalid runtime shape');
        return;
      }

      setRuntime(data);
      setErrorText('');
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Undo action failed';
      setErrorText(String(message));
    } finally {
      setScoreBusy(null);
    }
  };

  const confirmTitle =
    confirm === 'start'
      ? `${primaryActionLabel}?`
      : confirm === 'end'
      ? `${primaryActionLabel}?`
      : 'Reset game?';

  const sideNavButtons: Array<{
    key: string;
    label: string;
    active?: boolean;
    onClick?: () => void;
    variant?: 'contained' | 'outlined' | 'text';
    color?: 'primary' | 'inherit' | 'success' | 'error' | 'warning';
    disabled?: boolean;
  }> = [
    ...(isAdmin && primaryAction
      ? [{
          key: 'time',
          label: primaryActionLabel,
          active: runtime.inPlay,
          onClick: () => setConfirm(primaryAction),
          variant: 'contained' as const,
          color: primaryAction === 'start' ? 'success' as const : 'error' as const,
        }]
      : []),
    {
      key: 'score',
      label: showScorePanel ? 'Score live' : 'Score',
      active: showScorePanel,
      variant: 'outlined' as const,
      disabled: true,
    },
    {
      key: 'status',
      label: phaseLabel,
      active: true,
      variant: 'outlined' as const,
      disabled: true,
    },
    {
      key: 'posts',
      label: 'Posts',
      variant: 'outlined' as const,
      disabled: true,
    },
    {
      key: 'back',
      label: 'Back',
      onClick: () => navigate(-1),
      variant: 'text' as const,
    },
  ];

  const sportActionButtons = [
    {
      key: 'left-add',
      label: `+ ${leftTeamName}`,
      onClick: () => handleScore('left', 1, 'left-add'),
      disabled: scoreBusy !== null,
    },
    {
      key: 'right-add',
      label: `+ ${rightTeamName}`,
      onClick: () => handleScore('right', 1, 'right-add'),
      disabled: scoreBusy !== null,
    },
    {
      key: 'undo',
      label: '↶ Undo',
      onClick: handleUndo,
      disabled: scoreBusy !== null,
    },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f4f5f7',
        display: 'grid',
        gridTemplateColumns: { xs: '96px 1fr', sm: '112px 1fr', md: '132px 1fr' },
      }}
    >
      <Box
        sx={{
          bgcolor: '#eef1f5',
          borderRight: '1px solid #d8dde6',
          p: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          position: 'sticky',
          top: 0,
          height: '100vh',
          overflowY: 'auto',
        }}
      >
        {sideNavButtons.map((button) => (
          <Button
            key={button.key}
            fullWidth
            variant={button.variant || 'outlined'}
            color={button.color || 'inherit'}
            disabled={button.disabled}
            onClick={button.onClick}
            sx={{
              minHeight: 46,
              px: 0.75,
              py: 0.75,
              fontSize: '0.72rem',
              lineHeight: 1.15,
              textTransform: 'none',
              borderColor: button.active ? 'primary.main' : undefined,
            }}
          >
            {button.label}
          </Button>
        ))}

        {showScorePanel ? (
          <Paper sx={{ p: 1, mt: 0.5 }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 700 }}>
              Live score
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {sportActionButtons.map((button) => (
                <Button
                  key={button.key}
                  fullWidth
                  size="small"
                  variant={button.key === 'undo' ? 'outlined' : 'contained'}
                  disabled={button.disabled}
                  onClick={button.onClick}
                  sx={{
                    minHeight: 40,
                    px: 0.75,
                    py: 0.5,
                    fontSize: '0.72rem',
                    lineHeight: 1.1,
                    textTransform: 'none',
                  }}
                >
                  {button.label}
                </Button>
              ))}
            </Box>
          </Paper>
        ) : null}

        {isAdmin ? (
          <Button
            fullWidth
            variant="outlined"
            color="warning"
            onClick={() => setConfirm('reset')}
            sx={{ minHeight: 42, mt: 'auto', fontSize: '0.72rem', textTransform: 'none' }}
          >
            Reset
          </Button>
        ) : null}
      </Box>

      <Box sx={{ p: 1.25, overflow: 'auto' }}>
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'flex-start' },
              justifyContent: 'space-between',
              gap: { xs: 1, sm: 1.5 },
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.15rem' },
                  lineHeight: 1.2,
                  wordBreak: 'break-word',
                  mb: 0.5,
                }}
              >
                {titleText}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                {toTitleCase(runtime.gameType)}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                {periodSummary} · {runtime.inPlay ? 'Live' : phase === 'post' ? 'Finished' : 'Ready'}
              </Typography>

              {!!errorText && (
                <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                  {errorText}
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                textAlign: { xs: 'left', sm: 'right' },
                flexShrink: 0,
                alignSelf: { xs: 'flex-start', sm: 'flex-start' },
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  lineHeight: 1,
                  fontSize: { xs: '1.35rem', sm: '2.6rem' },
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 0.5, fontSize: { xs: '0.68rem', sm: '0.75rem' } }}>
                Refreshing every 5 seconds
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Annotation panel
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This wider area is ready for pitch-side play annotation buttons and sport-specific event controls.
          </Typography>
        </Paper>

        <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Recent actions
          </Typography>

          {runtime.log.length === 0 ? (
            <Typography variant="body2">No actions yet</Typography>
          ) : (
            <Box>
              {runtime.log
                .slice(-6)
                .reverse()
                .map((entry, index) => (
                  <Box key={`${entry.ts}-${index}`} sx={{ py: 0.75 }}>
                    <Typography variant="body2">
                      {new Date(entry.ts).toLocaleTimeString()} — {entry.action}
                      {entry.note ? ` (${entry.note})` : ''}
                    </Typography>
                    {index < Math.min(runtime.log.length, 6) - 1 ? <Divider sx={{ mt: 0.75 }} /> : null}
                  </Box>
                ))}
            </Box>
          )}
        </Paper>
      </Box>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
        <DialogTitle>{confirmTitle}</DialogTitle>

        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button onClick={handleConfirmedAction}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}